import { clsx } from 'clsx';
import { DropdownMenu } from 'radix-ui';
import { useId, type ReactNode } from 'react';

/* -------------------------------------------------------------------------
   ContextSwitcher

   Organization / Product / Environment (§31). Built on Radix's menu rather than
   a hand-rolled popover so that focus trapping, roving tab index, type-ahead,
   Escape handling and `aria-expanded` are correct without us re-deriving them.

   `unavailableReason` matters: a control that silently does nothing is worse
   than one that says why.

   WHY aria-disabled RATHER THAN disabled

   The reason used to live only in `title`. A native `disabled` button cannot be
   focused, so a keyboard user could never reach the control to hear it, and
   `title` is not reliably announced by screen readers even when reachable — the
   explanation existed for mouse users hovering, and for nobody else.

   The control is therefore focusable and marked `aria-disabled`, with the reason
   wired through `aria-describedby` so it is announced on focus. It still refuses
   to open: `aria-disabled` describes a control that is present and inert, which
   is exactly what this is. `title` stays as a supplement, never as the channel.
   ------------------------------------------------------------------------- */

export interface ContextOption {
  id: string;
  label: string;
  description?: string;
  badge?: ReactNode;
}

export interface ContextSwitcherProps {
  /** Short label above the value, e.g. "Organization". */
  kind: string;
  value: ContextOption | null;
  options: readonly ContextOption[];
  onSelect?: (id: string) => void;
  placeholder?: string;
  /**
   * Why this selector cannot be used right now. Rendered as an accessible
   * description, not only as a tooltip.
   */
  unavailableReason?: string;
  /**
   * Distinct from having no options: the parent's list is still being fetched.
   * "There are none" and "we have not asked yet" are different facts and the
   * bar must not state the first while the second is true.
   */
  loading?: boolean;
  /**
   * Offered as an explicit menu entry when something is selected. Returning to
   * "none" has to be reachable; otherwise a selection made once is permanent
   * for the session, and an environment nobody meant to keep becomes an implied
   * default for everything that reads context afterwards.
   */
  onClear?: () => void;
  clearLabel?: string;
  icon?: ReactNode;
  className?: string;
}

const MENU_SURFACE =
  'z-50 min-w-[248px] overflow-hidden rounded-[var(--vc-radius-lg)] border ' +
  'border-[var(--vc-border)] bg-[var(--vc-surface-raised)] p-1 shadow-[var(--vc-shadow-lg)]';

export function ContextSwitcher({
  kind,
  value,
  options,
  onSelect,
  placeholder = 'None selected',
  unavailableReason,
  loading = false,
  onClear,
  clearLabel = 'Clear selection',
  icon,
  className,
}: ContextSwitcherProps) {
  const reasonId = useId();
  const reason = loading ? undefined : unavailableReason;
  const isUnavailable = loading || Boolean(reason) || options.length === 0;
  const shownValue = loading ? null : (value?.label ?? placeholder);

  const trigger = (
    <button
      type="button"
      aria-disabled={isUnavailable || undefined}
      aria-haspopup={isUnavailable ? undefined : 'menu'}
      {...(reason ? { 'aria-describedby': reasonId } : {})}
      onClick={isUnavailable ? (event) => event.preventDefault() : undefined}
      onKeyDown={
        isUnavailable
          ? (event) => {
              // Space and Enter would otherwise activate the trigger Radix put
              // on this element. Focusable and inert, not focusable and armed.
              if (event.key === 'Enter' || event.key === ' ') event.preventDefault();
            }
          : undefined
      }
      aria-label={`${kind}: ${loading ? 'loading' : (value?.label ?? placeholder)}`}
      title={reason}
      className={clsx(
        'group flex h-[30px] max-w-[230px] min-w-0 items-center gap-2 rounded-[var(--vc-radius-md)]',
        'border border-transparent px-2 text-left',
        'transition-colors duration-[var(--vc-duration-fast)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vc-focus-ring)]',
        /*
          No blanket opacity.

          `opacity-70` dimmed the whole control, and the small uppercase kind
          label is already a muted token — composited against the bar it landed
          at 3.14:1, under AA, while the token itself measured 5.11:1. A
          token-level contrast test cannot see that, because the failure is
          introduced by the opacity, not by the colour.

          Unavailability is carried by the cursor, the absent hover affordance
          and `aria-disabled`. None of those cost legibility.
        */
        isUnavailable
          ? 'cursor-not-allowed'
          : clsx(
              'cursor-pointer hover:border-[var(--vc-border)] hover:bg-[var(--vc-surface-hover)]',
              'data-[state=open]:border-[var(--vc-border)] data-[state=open]:bg-[var(--vc-surface-hover)]',
            ),
        className,
      )}
    >
      {icon ? (
        <span aria-hidden="true" className="shrink-0 text-[var(--vc-text-subtle)] [&>svg]:size-3.5">
          {icon}
        </span>
      ) : null}

      <span className="flex min-w-0 flex-col leading-none">
        <span className="text-[9.5px] font-medium tracking-[0.06em] text-[var(--vc-text-subtle)] uppercase">
          {kind}
        </span>
        <span
          className={clsx(
            'mt-1 truncate text-[12.5px] font-medium',
            value && !loading ? 'text-[var(--vc-text)]' : 'text-[var(--vc-text-subtle)]',
          )}
        >
          {loading ? `Loading ${kind.toLowerCase()}…` : (shownValue ?? placeholder)}
        </span>
      </span>

      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        className="ml-auto size-3 shrink-0 text-[var(--vc-text-subtle)]"
      >
        <path
          d="m7 10 5 5 5-5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  if (isUnavailable) {
    return (
      <>
        {trigger}
        {reason ? (
          <span id={reasonId} className="sr-only">
            {reason}
          </span>
        ) : null}
      </>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={MENU_SURFACE} sideOffset={6} align="start">
          <DropdownMenu.Label className="px-2 py-1.5 text-[10px] font-medium tracking-[0.06em] text-[var(--vc-text-subtle)] uppercase">
            {kind}
          </DropdownMenu.Label>

          {onClear && value ? (
            <>
              <DropdownMenu.Item
                onSelect={() => onClear()}
                className={clsx(
                  'flex cursor-pointer items-center gap-2 rounded-[var(--vc-radius-md)] px-2 py-1.5',
                  'text-[13px] text-[var(--vc-text-muted)] outline-none select-none',
                  'data-[highlighted]:bg-[var(--vc-surface-hover)]',
                )}
              >
                {clearLabel}
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-[var(--vc-border)]" />
            </>
          ) : null}

          {options.map((option) => (
            <DropdownMenu.Item
              key={option.id}
              onSelect={() => onSelect?.(option.id)}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded-[var(--vc-radius-md)] px-2 py-1.5',
                'text-[13px] text-[var(--vc-text)] outline-none select-none',
                'data-[highlighted]:bg-[var(--vc-surface-hover)]',
              )}
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{option.label}</span>
                {option.description ? (
                  <span className="truncate text-[11px] text-[var(--vc-text-subtle)]">
                    {option.description}
                  </span>
                ) : null}
              </span>
              {option.badge ? <span className="ml-auto shrink-0">{option.badge}</span> : null}
              {option.id === value?.id ? (
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="ml-auto size-3.5 shrink-0 text-[var(--vc-accent)]"
                >
                  <path
                    d="m5 12.5 4.5 4.5L19 7.5"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : null}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
